import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { KeycloakProtectedRoute } from './components/common/keycloak-protected-route';
import { ReconciliationFlagGuard } from './components/common/reconciliation-flag-guard';
import { RegistrationFlagGuard } from './components/common/registration-flag-guard';
import { DropdownOptionsProvider } from './contexts/dropdown-options-context';
import { FeatureFlagProvider } from './contexts/feature-flag-context';
import { KeycloakAuthProvider } from './contexts/keycloak-auth-context';
import { SupplierDisplayProvider } from './contexts/supplier-display-context';
import { AppLayout } from './layouts/app-layout';
import { ROUTE_ROLES } from './lib/authz';
import { ClientDetail } from './pages/client-detail';
import { Clients } from './pages/clients';
import { CurrentState } from './pages/current-state';
import { Dashboard } from './pages/dashboard';
import { Escrow } from './pages/escrow';
import { FeatureFlags } from './pages/feature-flags';
import { FileFormatDetail } from './pages/file-format-detail';
import { FileFormatsPage } from './pages/file-formats';
import { Home } from './pages/home';
import { PartnerDetail } from './pages/partner-detail';
import { Partners } from './pages/partners';
import { PaymentDetail } from './pages/payment-detail';
import { PaymentFileDetail } from './pages/payment-file-detail';
import { PaymentFiles } from './pages/payment-files';
import { Payments } from './pages/payments';
import { ProspectCompanies } from './pages/prospect-companies';
import { ProspectModule } from './pages/prospect-module';
import { ProviderDetail } from './pages/provider-detail';
import { Providers } from './pages/providers';
import { Reconciliation } from './pages/reconciliation';
import { ReferenceAdminPage } from './pages/reference-admin';
import { Register } from './pages/register';
import { RegisterBusiness } from './pages/register-business';
import { RegisterIndividual } from './pages/register-individual';
import { RegistrationSuccess } from './pages/registration-success';
import { Reports } from './pages/reports';
import { Settings } from './pages/settings';
import { SupplierDetail } from './pages/supplier-detail';
import { Suppliers } from './pages/suppliers';
import { TermsOfUse } from './pages/terms-of-use';
import { Users } from './pages/users';

function App() {
  return (
    <KeycloakAuthProvider>
      <FeatureFlagProvider>
        <SupplierDisplayProvider>
          <DropdownOptionsProvider>
            <Router>
              <Routes>
                {/* Public homepage - serves as login entry point */}
                <Route path="/" element={<Home />} />
                {/* Public registration pages - guarded by feature flag */}
                <Route
                  path="/register"
                  element={
                    <RegistrationFlagGuard>
                      <Register />
                    </RegistrationFlagGuard>
                  }
                />
                <Route
                  path="/register/business"
                  element={
                    <RegistrationFlagGuard>
                      <RegisterBusiness />
                    </RegistrationFlagGuard>
                  }
                />
                <Route
                  path="/register/individual"
                  element={
                    <RegistrationFlagGuard>
                      <RegisterIndividual />
                    </RegistrationFlagGuard>
                  }
                />
                <Route
                  path="/registration-success"
                  element={
                    <RegistrationFlagGuard>
                      <RegistrationSuccess />
                    </RegistrationFlagGuard>
                  }
                />
                <Route path="/terms-of-use" element={<TermsOfUse />} />

                {/* Protected dashboard route */}
                <Route
                  path="/dashboard"
                  element={
                    <KeycloakProtectedRoute>
                      <AppLayout />
                    </KeycloakProtectedRoute>
                  }
                >
                  <Route index element={<Dashboard />} />
                </Route>

                {/* Protected app routes with persistent sidebar */}
                <Route
                  path="/"
                  element={
                    <KeycloakProtectedRoute>
                      <AppLayout />
                    </KeycloakProtectedRoute>
                  }
                >
                  <Route
                    path="clients"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.ANY_INTERNAL}>
                        <Clients />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="clients/:id"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.ANY_INTERNAL}>
                        <ClientDetail />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="escrow"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.ANY_INTERNAL}>
                        <Escrow />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route path="payments" element={<Payments />} />
                  <Route path="payments/:id" element={<PaymentDetail />} />
                  <Route path="payment-files" element={<PaymentFiles />} />
                  <Route path="payment-files/:id" element={<PaymentFileDetail />} />
                  <Route path="suppliers" element={<Suppliers />} />
                  <Route path="suppliers/:id" element={<SupplierDetail />} />
                  <Route
                    path="reconciliation"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.ANY_INTERNAL}>
                        <ReconciliationFlagGuard>
                          <Reconciliation />
                        </ReconciliationFlagGuard>
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="reports"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.ANY_INTERNAL}>
                        <Reports />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="users"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <Users />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="providers"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <Providers />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="providers/:partyId"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <ProviderDetail />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="partners"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <Partners />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="partners/:partyId"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <PartnerDetail />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="file-formats"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <FileFormatsPage />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="file-formats/:id"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <FileFormatDetail />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="reference-admin"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <ReferenceAdminPage />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="prospect-companies"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <ProspectCompanies />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="prospect-module"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <ProspectModule />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="prospect-module/:companyId"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <ProspectModule />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route
                    path="prospect-module/current-state"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <CurrentState />
                      </KeycloakProtectedRoute>
                    }
                  />
                  <Route path="settings" element={<Settings />} />
                  <Route
                    path="feature-flags"
                    element={
                      <KeycloakProtectedRoute roles={ROUTE_ROLES.INTERNAL_ADMIN} requireAllRoles>
                        <FeatureFlags />
                      </KeycloakProtectedRoute>
                    }
                  />
                </Route>

                {/* Unauthorized page */}
                <Route
                  path="/unauthorized"
                  element={
                    <div className="min-h-screen flex items-center justify-center bg-background">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-foreground mb-4">Access Denied</h1>
                        <p className="text-muted-foreground mb-4">
                          You don't have permission to access this resource.
                        </p>
                        <button
                          type="button"
                          onClick={() => window.history.back()}
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                          Go Back
                        </button>
                      </div>
                    </div>
                  }
                />
              </Routes>
            </Router>
          </DropdownOptionsProvider>
        </SupplierDisplayProvider>
      </FeatureFlagProvider>
    </KeycloakAuthProvider>
  );
}

export default App;
